import { Component, OnInit, OnDestroy, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, RouterModule, Router, NavigationEnd, NavigationError } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { GlobalSearchComponent } from './core/components/global-search/global-search.component';
import { AuthService } from './core/services/auth.service';
import { NavigationService } from './core/services/navigation.service';
import { InitializationService } from './core/services/initialization.service';
import { SitePanelNavComponent, NavItem } from './core/components/site-panel-nav/site-panel-nav.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { isChunkLoadError, reloadOnceForStaleAssets } from './core/utils/chunk-load.util';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterModule,
    MatIconModule,
    MatMenuModule,
    SitePanelNavComponent,
    GlobalSearchComponent,
  ],
  template: `
    <!-- Overlay de Carga -->
    <div class="ds-loading-overlay" *ngIf="!isInitialized && !shellError">
      <div class="ds-spinner-box">
        <mat-icon class="ds-spin">refresh</mat-icon>
        <p>Iniciando sistema BDL...</p>
      </div>
    </div>

    <div class="ds-shell-error" *ngIf="shellError" role="alert">
      <div class="ds-shell-error__box">
        <h2>No se pudo abrir BDL</h2>
        <p>{{ shellError }}</p>
        <div class="ds-shell-error__actions">
          <button type="button" (click)="retryShell()">Reintentar</button>
          <button type="button" (click)="reloadApp()">Recargar</button>
        </div>
      </div>
    </div>

    <!-- Un solo router-outlet. Cambiar de outlet al guardar el token cancelaba
         la navegación al dashboard y dejaba la pantalla en blanco. -->
    <div class="ds-app-layout" *ngIf="isInitialized && !shellError" [class.ds-app-layout--public]="!authService.isAuthenticated()">
      
      <!-- Header Superior Moderno -->
      <header class="ds-header" *ngIf="authService.isAuthenticated()">
        <div class="header-left">
          <button class="header-icon-btn nav-trigger" (click)="toggleNav()" *ngIf="isMobile()">
            <mat-icon>menu</mat-icon>
          </button>
          <a routerLink="/" class="ds-logo">
            <div class="logo-circle">
              <mat-icon>business</mat-icon>
            </div>
            <div class="logo-text">
              <span class="brand-name">BDL</span>
              <span class="brand-sub">Management</span>
            </div>
          </a>
        </div>

        <div class="header-center">
          <div class="search-wrapper">
            <app-global-search></app-global-search>
          </div>
        </div>

        <div class="header-right">
          <div class="user-control">
            <button class="user-pill" [matMenuTriggerFor]="userMenu">
              <div class="user-avatar">
                {{ (authService.getCurrentUser()?.username || 'U').charAt(0).toUpperCase() }}
              </div>
              <span class="user-label" *ngIf="!isMobile()">
                {{ authService.getCurrentUser()?.username || 'Usuario' }}
              </span>
              <mat-icon class="chevron">expand_more</mat-icon>
            </button>

            <mat-menu #userMenu="matMenu" class="ds-user-dropdown" xPosition="before">
              <div class="dropdown-header">
                <p class="name">{{ authService.getCurrentUser()?.username }}</p>
                <p class="role">Administrador BDL</p>
              </div>
              <div class="ds-divider"></div>
              <button mat-menu-item routerLink="/account">
                <mat-icon>settings</mat-icon>
                <span>Mi Cuenta</span>
              </button>
              <button mat-menu-item (click)="logout()" class="logout-item">
                <mat-icon>logout</mat-icon>
                <span>Cerrar Sesión</span>
              </button>
            </mat-menu>
          </div>
        </div>
      </header>

      <div class="ds-main-container">
        <!-- Sidebar -->
        <aside class="ds-aside" *ngIf="authService.isAuthenticated()" [class.collapsed]="navCollapsed" [class.mobile-open]="navOpen">
          <app-site-panel-nav
            [collapsed]="navCollapsed"
            [isOpen]="navOpen"
            (navItemClick)="onNavItemClick($event)"
          ></app-site-panel-nav>
        </aside>

        <!-- Contenido -->
        <main class="ds-content" (click)="onMainContentClick($event)">
          <div class="ds-route-error" *ngIf="routeError">
            <span>{{ routeError }}</span>
            <button type="button" class="ds-route-error__retry" (click)="retryFailedRoute()">Reintentar</button>
          </div>
          <div class="content-viewport">
            <router-outlet></router-outlet>
          </div>
        </main>
      </div>
    </div>
  `,
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private lastActivityTime = Date.now();
  private lastSessionRefresh = 0;
  private activityCheckInterval?: number;
  private readonly activityEvents = ['click', 'keydown', 'mousemove', 'scroll'] as const;
  private readonly onActivity = () => this.noteUserActivity();
  /** Session timeout is 8h; refreshing it once a minute is enough and avoids CD on every key. */
  private readonly sessionRefreshIntervalMs = 60_000;

  navCollapsed = false;
  navOpen = false;
  isInitialized = false;
  shellError: string | null = null;
  routeError: string | null = null;
  private failedRouteUrl: string | null = null;
  private initWatch?: number;
  private postLoginWatch?: number;
  private postLoginSettled = false;

  constructor(
    public authService: AuthService,
    private navigationService: NavigationService,
    private initializationService: InitializationService,
    private router: Router,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this.bindActivityListeners();
    this.armInitWatchdog();
    this.authService.currentUser$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.authService.isAuthenticated()) {
        this.armPostLoginWatch();
        return;
      }
      this.postLoginSettled = false;
      this.clearPostLoginWatch();
    });
    this.initializationService.waitForReady().then(() => {
      try {
        this.initializeNavigation();
        this.setupRouteChangeHandling();
        this.setupKeyboardHandling();
        this.startActivityMonitoring();
      } catch (error) {
        console.error('Shell initialization failed', error);
        this.shellError = 'No se pudo preparar la barra lateral ni la página principal.';
      } finally {
        this.isInitialized = true;
        window.clearTimeout(this.initWatch);
      }
    }).catch((error) => {
      console.error('App readiness failed', error);
      this.isInitialized = true;
      this.shellError = 'No se pudo preparar la barra lateral ni la página principal.';
    });
  }

  ngOnDestroy(): void {
    this.unbindActivityListeners();
    window.clearTimeout(this.initWatch);
    this.clearPostLoginWatch();
    this.destroy$.next();
    this.destroy$.complete();
    this.stopActivityMonitoring();
  }

  private armInitWatchdog(): void {
    this.initWatch = window.setTimeout(() => {
      if (this.isInitialized || this.shellError) return;
      this.shellError = 'La aplicación no terminó de iniciar.';
    }, 8000);
  }

  /**
   * Login stores the token and then navigates to /dashboard. If that navigation
   * is cancelled or never ends, the URL stays on /login with an empty outlet.
   */
  private armPostLoginWatch(): void {
    if (this.postLoginSettled || this.postLoginWatch) return;
    this.postLoginWatch = window.setTimeout(() => {
      this.postLoginWatch = undefined;
      if (!this.authService.isAuthenticated() || this.shellError || this.postLoginSettled) return;
      const url = this.router.url || '';
      if (url.includes('/login') || url === '/' || url === '') {
        this.shellError = 'La sesión inició, pero la pantalla principal no cargó.';
      }
    }, 8000);
  }

  private clearPostLoginWatch(): void {
    window.clearTimeout(this.postLoginWatch);
    this.postLoginWatch = undefined;
  }

  retryShell(): void {
    this.shellError = null;
    this.postLoginSettled = false;
    this.isInitialized = true;
    const target = this.authService.isAuthenticated() ? '/dashboard' : '/login';
    this.router.navigateByUrl(target).then((ok) => {
      if (ok === false) {
        this.shellError = 'No se pudo abrir la aplicación.';
      }
    }).catch((error) => {
      console.error('Shell retry failed', error);
      this.shellError = 'No se pudo abrir la aplicación.';
    });
    if (this.authService.isAuthenticated()) this.armPostLoginWatch();
  }

  reloadApp(): void {
    window.location.reload();
  }

  /**
   * Listeners stay outside Angular. HostListener on the root component marked
   * the whole tree dirty on every keydown and mousemove, so search inputs felt
   * late. The session clock only re-enters the zone once a minute.
   */
  private bindActivityListeners(): void {
    this.ngZone.runOutsideAngular(() => {
      for (const eventName of this.activityEvents) {
        document.addEventListener(eventName, this.onActivity, { passive: true });
      }
    });
  }

  private unbindActivityListeners(): void {
    for (const eventName of this.activityEvents) {
      document.removeEventListener(eventName, this.onActivity);
    }
  }

  private noteUserActivity(): void {
    this.lastActivityTime = Date.now();
    if (!this.authService.isAuthenticated()) return;
    if (this.lastActivityTime - this.lastSessionRefresh < this.sessionRefreshIntervalMs) return;
    this.lastSessionRefresh = this.lastActivityTime;
    this.ngZone.run(() => {
      if (this.authService.isAuthenticated()) {
        this.authService.refreshSession();
      }
    });
  }

  private startActivityMonitoring(): void {
    this.stopActivityMonitoring();
    if (!this.authService.isAuthenticated() || !this.authService.isServiceReady()) return;

    this.activityCheckInterval = window.setInterval(() => {
      try {
        if (!this.authService.isServiceReady()) {
          this.stopActivityMonitoring();
          return;
        }
        const now = Date.now();
        if (now - this.lastActivityTime > 30 * 60 * 1000 && this.authService.isAuthenticated()) {
          this.authService.refreshSession();
        }
      } catch (error) {
        this.stopActivityMonitoring();
      }
    }, 5 * 60 * 1000);
  }

  private stopActivityMonitoring(): void {
    if (this.activityCheckInterval) {
      clearInterval(this.activityCheckInterval);
      this.activityCheckInterval = undefined;
    }
  }

  private initializeNavigation(): void {
    this.navigationService.isOpen$.pipe(takeUntil(this.destroy$)).subscribe(isOpen => this.navOpen = isOpen);
    this.navigationService.isCollapsed$.pipe(takeUntil(this.destroy$)).subscribe(isCollapsed => this.navCollapsed = isCollapsed);
  }

  private setupRouteChangeHandling(): void {
    this.router.events.pipe(
      takeUntil(this.destroy$)
    ).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.routeError = null;
        this.failedRouteUrl = null;
        const url = event.urlAfterRedirects || event.url;
        if (this.authService.isAuthenticated() && !url.includes('/login')) {
          this.postLoginSettled = true;
          this.clearPostLoginWatch();
          this.shellError = null;
        }
        if (this.isMobile()) this.navigationService.close();
        return;
      }
      if (!(event instanceof NavigationError)) return;

      // Lazy chunk 404 after a deploy: reload once, then keep the shell and explain it.
      if (isChunkLoadError(event.error) && reloadOnceForStaleAssets()) {
        return;
      }
      this.failedRouteUrl = event.url;
      this.routeError = 'No se pudo cargar esta sección. El resto de la aplicación sigue disponible.';
      const url = this.router.url || '';
      if (this.authService.isAuthenticated() && (url.includes('/login') || url === '/' || url === '')) {
        this.shellError = 'La sesión inició, pero la pantalla principal no cargó.';
      }
      console.error('Navigation error', event.url, event.error);
    });
  }

  retryFailedRoute(): void {
    const url = this.failedRouteUrl || this.router.url;
    this.routeError = null;
    void this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      void this.router.navigateByUrl(url);
    });
  }

  private setupKeyboardHandling(): void {
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.navOpen) this.closeNav();
    });
  }

  toggleNav(): void { this.navigationService.toggle(); }

  onNavItemClick(item: NavItem): void {
    if (this.isMobile()) this.closeNav();
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  closeNav(): void { this.navigationService.close(); }

  isMobile(): boolean { return window.innerWidth <= 768; }

  onMainContentClick(event: MouseEvent): void {
    if (!this.navOpen) return;
    const aside = document.querySelector('.ds-aside');
    const trigger = document.querySelector('.nav-trigger');
    if (aside && !aside.contains(event.target as Node) && trigger && !trigger.contains(event.target as Node)) {
      this.closeNav();
    }
  }
}
